import { z } from 'zod';
import { resolveAddressCountryRules, isKnownCountryCode } from './Country.js';

//--------------------------------
// Address Schema
//--------------------------------
const isFilled = (field) => typeof field === 'string' && field.trim().length > 0;
/**
 * Canonical address shape.
 *
 * Which fields are required depends on the country: a street line and a city
 * always, an administrative area and a postal code only where that country has
 * them. `country` stays optional and an absent value resolves to the US, so
 * addresses stored before the country registry existed validate exactly as
 * they did before. See `resolveAddressCountryRules`.
 */
const addressSchema = z
    .object({
    addressLineOne: z.string(),
    addressLineTwo: z.string(),
    city: z.string(),
    state: z.string(),
    zip: z.string(),
    zipBase: z.string().optional(),
    zipExtension: z.string().optional(),
    county: z.string().optional(),
    lat: z.number().optional(),
    lng: z.number().optional(),
    country: z.string().optional(),
    addressString: z.string().optional(),
})
    .superRefine((value, ctx) => {
    const rules = resolveAddressCountryRules(value.country);
    // An unrecognized country would otherwise resolve to the permissive generic
    // entry, so a typo on a US address would quietly stop requiring its state
    // and postal code rather than failing.
    if (!isKnownCountryCode(value.country)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Enter a valid country' });
    }
    if (!isFilled(value.addressLineOne)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Enter a valid address line one' });
    }
    if (!isFilled(value.city)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Enter a valid city' });
    }
    if (rules.isAdminAreaRequired && !isFilled(value.state)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Enter a valid ${rules.adminAreaLabel.toLowerCase()}`,
        });
    }
    if (rules.isPostalCodeRequired && !isFilled(value.zip)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Enter a valid ${rules.postalCodeLabel.toLowerCase()}`,
        });
    }
    if (rules.postalCodePattern &&
        isFilled(value.zip) &&
        !rules.postalCodePattern.test(value.zip.trim())) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Enter a valid ${rules.postalCodeLabel.toLowerCase()}`,
        });
    }
});

export { addressSchema };
//# sourceMappingURL=Address.js.map
